import { useState, type ReactNode } from "react";
import { Check, LoaderCircle, AlertTriangle } from "lucide-react";
import { Modal } from "./Dialog";

/**
 * Asks before a consequential change. Says what will happen and what is
 * kept (history is never deleted), optionally asks for a reason (stored in
 * the record's history) or for the record's name to be typed first. Stays
 * open with the error when the change fails; cannot be dismissed while it
 * runs.
 */
export function ConfirmDialog({
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  tone = "default",
  reason,
  typeToConfirm,
  onConfirm,
  onClose,
  children,
}: {
  title: string;
  description: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** "danger" for voiding, archiving, removing access and the like. */
  tone?: "default" | "danger";
  /** Ask why; `required` blocks confirming without one. */
  reason?: { label?: string; placeholder?: string; required?: boolean };
  /** The exact text (e.g. the record's name) to type before confirming. */
  typeToConfirm?: string;
  onConfirm: (reason: string) => Promise<unknown> | unknown;
  onClose: () => void;
  children?: ReactNode;
}) {
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [why, setWhy] = useState(""),
    [typed, setTyped] = useState("");
  const blocked =
    busy ||
    (!!reason?.required && !why.trim()) ||
    (!!typeToConfirm && typed.trim().toLowerCase() !== typeToConfirm.trim().toLowerCase());
  return (
    <Modal title={title} onClose={onClose} dismissible={!busy}>
      <form
        className="confirm-dialog"
        onSubmit={async (e) => {
          e.preventDefault();
          if (blocked) return;
          setBusy(true);
          setError("");
          try {
            await onConfirm(why.trim());
            onClose();
          } catch (err) {
            setError(err instanceof Error ? err.message : "That did not work. Check your connection and try again.");
          } finally {
            setBusy(false);
          }
        }}
      >
        <div className={`confirm-copy${tone === "danger" ? " danger" : ""}`}>
          {tone === "danger" && <AlertTriangle size={18} aria-hidden="true" />}
          <div>{description}</div>
        </div>
        {children}
        {reason && (
          <label className="field">
            <span>
              {reason.label ?? "Reason"}
              {reason.required && <b> *</b>}
            </span>
            <textarea
              data-autofocus
              value={why}
              required={reason.required}
              placeholder={reason.placeholder}
              onChange={(e) => setWhy(e.target.value)}
              rows={2}
            />
            <small>Saved in this record's history.</small>
          </label>
        )}
        {typeToConfirm && (
          <label className="field">
            <span>
              Type <b>{typeToConfirm}</b> to confirm
            </span>
            <input value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" {...(!reason ? { "data-autofocus": true } : {})} />
          </label>
        )}
        {error && (
          <div role="alert" className="error-box">
            {error}
          </div>
        )}
        <footer className="form-footer">
          <button type="button" className="button secondary" onClick={onClose} disabled={busy}>
            {cancelLabel}
          </button>
          <button type="submit" className={`button${tone === "danger" ? " danger" : ""}`} disabled={blocked}>
            {busy ? <LoaderCircle className="spin" size={17} /> : <Check size={17} />} {busy ? "Working…" : confirmLabel}
          </button>
        </footer>
      </form>
    </Modal>
  );
}
